import "./edit-commodity-modal.css";

import { useState } from "react";

import { useUpdateSavedCommodity } from "~/features/commodities/hooks/use-commodities.ts";
import { useQuoteDetailsPageLoadQueries } from "~/features/quote/hooks/use-quote-details-hooks.ts";
import type { InputGroupValue } from "~/shared/components/input-group.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import type {
	Commodity,
	CommodityUpdateBody,
} from "~/shared/schemas/commodity-schema.ts";
import { commodityUpdateSchema } from "~/shared/schemas/commodity-schema.ts";
import {
	calculateItemDensity,
	getEstClass,
} from "~/shared/utils/commodity-utils.ts";

import type {
	CommodityFormField,
	CommodityFormValues,
} from "./commodity-form.tsx";
import { CommodityForm } from "./commodity-form.tsx";

const COMMODITY_FORM_FIELD_TO_DRAFT_FIELD: Partial<
	Record<CommodityFormField, keyof CommodityUpdateBody>
> = {
	description: "description",
	quantity: "quantity",
	unit: "unitTypeId",
	weight: "weight",
	length: "dimensionLength",
	width: "dimensionWidth",
	height: "dimensionHeight",
	class: "freightClassId",
	nmfc: "nationalMotorFreightClassification",
	isStackable: "isStackable",
	isHazmat: "isHazmat",
};

type EditCommodityModalProps = {
	commodity: Commodity;
	onClose: () => void;
};

export const EditCommodityModal = ({
	commodity,
	onClose,
}: EditCommodityModalProps) => {
	const isMobile = useIsMobile();
	const [draft, setDraft] = useState(() =>
		commodityUpdateSchema.parse({
			...commodity,
			nationalMotorFreightClassification: commodity.nmfc,
		}),
	);
	const { data } = useQuoteDetailsPageLoadQueries();
	const { mutate, isPending } = useUpdateSavedCommodity();
	const { showSuccess, showError } = useNotificationActions();

	const density = calculateItemDensity({
		quantity: draft.quantity,
		weight: draft.weight,
		length: draft.dimensionLength,
		width: draft.dimensionWidth,
		height: draft.dimensionHeight,
	});
	const estimatedClass = getEstClass(density);
	const formValues: CommodityFormValues = {
		id: draft.id,
		description: draft.description ?? "",
		quantity: draft.quantity,
		unit: data?.unitTypes?.find((unit) => unit.id === draft.unitTypeId) ?? null,
		weight: draft.weight,
		length: draft.dimensionLength,
		width: draft.dimensionWidth,
		height: draft.dimensionHeight,
		class:
			data?.freightClassData?.find(
				(freightClass) => freightClass.id === draft.freightClassId,
			) ?? null,
		nmfc: draft.nationalMotorFreightClassification ?? "",
		pieceCount: null,
		density,
		estClass: estimatedClass,
		isStackable: !!draft.isStackable,
		isHazmat: !!draft.isHazmat,
	};

	const handleChange = (field: CommodityFormField, value: InputGroupValue) => {
		const draftField = COMMODITY_FORM_FIELD_TO_DRAFT_FIELD[field];
		if (!draftField) {
			showError("Unable to update commodity field. Please try again.");
			return;
		}
		let normalizedValue = value;
		if (field === "unit" || field === "class") {
			normalizedValue =
				value && typeof value === "object" && "id" in value
					? Number(value["id"])
					: null;
		}
		const result = commodityUpdateSchema.safeParse({
			...draft,
			[draftField]: normalizedValue,
		});
		if (!result.success) {
			showError("Unable to update commodity field. Please try again.");
			return;
		}
		setDraft(result.data);
	};

	const handleSave = () => {
		mutate(draft, {
			onSuccess: () => {
				showSuccess("Commodity updated in saved commodities.");
				onClose();
			},
			onError: (error) => showError(error.message),
		});
	};

	return (
		<TraxModal
			className={"edit-commodity-modal"}
			confirmText={isMobile ? "Save" : "Save Edits"}
			onCancel={onClose}
			onClose={onClose}
			onConfirm={handleSave}
			showButtonsDisabled={isPending}
			showConfirmationButtons={true}
			title={"Edit Commodity"}
			width={isMobile ? "100%" : undefined}
		>
			<CommodityForm
				disabled={isPending}
				freightClassData={data?.freightClassData}
				groupId={draft.id}
				isMobile={isMobile}
				mode={"edit"}
				onChange={handleChange}
				showCalculatedFields={isMobile}
				showCommodityFlags={true}
				unitTypes={data?.unitTypes}
				values={formValues}
			/>
		</TraxModal>
	);
};
